import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Search,
  Phone,
  ArrowRight,
  Home,
  MapPin,
  PenTool,
  TrendingUp,
  Star,
  BookOpen,
  Mail,
} from "lucide-react";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { logNotFound, lookupRedirect } from "@/lib/redirects.functions";
import { STATIC_REDIRECTS } from "@/lib/staticRedirects";

type QuickLink = {
  to: string;
  label: string;
  desc: string;
  icon: typeof Home;
};

const TOP_DESTINATIONS: QuickLink[] = [
  {
    to: "/colorado-springs-web-design",
    label: "Web Design",
    desc: "Conversion-built sites for Colorado businesses.",
    icon: PenTool,
  },
  {
    to: "/colorado-springs-seo",
    label: "SEO",
    desc: "Rank locally and get more qualified leads.",
    icon: TrendingUp,
  },
  {
    to: "/colorado-springs-google-maps",
    label: "Google Maps",
    desc: "Win the 3-pack in your service area.",
    icon: MapPin,
  },
  {
    to: "/pricing",
    label: "Pricing",
    desc: "Transparent packages and add-ons across every service.",
    icon: Star,
  },
  {
    to: "/learn",
    label: "Learning Hub",
    desc: "Guides, podcasts, and real answers.",
    icon: BookOpen,
  },
  {
    to: "/contact",
    label: "Contact",
    desc: "Talk to a real Colorado human.",
    icon: Mail,
  },
];

const POPULAR_CITIES: { slug: string; name: string }[] = [
  { slug: "colorado-springs", name: "Colorado Springs" },
  { slug: "denver", name: "Denver" },
  { slug: "boulder", name: "Boulder" },
  { slug: "fort-collins", name: "Fort Collins" },
  { slug: "pueblo", name: "Pueblo" },
  { slug: "castle-rock", name: "Castle Rock" },
];

const CITY_SERVICES: { suffix: string; label: string; icon: typeof Home }[] = [
  { suffix: "web-design", label: "Web Design", icon: PenTool },
  { suffix: "seo", label: "SEO", icon: TrendingUp },
  { suffix: "google-maps", label: "Google Maps", icon: MapPin },
];

export function NotFoundPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState<{ type: "error" | "info"; message: string } | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // 404 pages should not be indexed. Set the title + a robots meta tag on
  // mount; clean both up on unmount so other routes are unaffected.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prevTitle = document.title;
    document.title = "Page Not Found (404) — Colorado Web Impressions";

    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    meta.setAttribute("data-not-found", "true");
    document.head.appendChild(meta);

    return () => {
      document.title = prevTitle;
      meta.remove();
    };
  }, []);

  // Log the 404 and check for a configured redirect; if there is one, send
  // the visitor to the mapped page immediately.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname + window.location.search;
    const referrer = document.referrer || null;
    const userAgent = navigator.userAgent || null;
    let cancelled = false;

    // Static redirect map runs first — instant, no network roundtrip.
    const staticKey = window.location.pathname.replace(/\/$/, "") || "/";
    const staticTo = STATIC_REDIRECTS[staticKey] ?? STATIC_REDIRECTS[staticKey.toLowerCase()];
    if (staticTo) {
      setRedirecting(true);
      // fire-and-forget logging
      logNotFound({ data: { path, referrer, userAgent } }).catch(() => null);
      window.location.replace(staticTo);
      return;
    }

    (async () => {
      try {
        const [, lookup] = await Promise.all([
          logNotFound({ data: { path, referrer, userAgent } }).catch(() => null),
          lookupRedirect({ data: { path } }).catch(() => ({ match: null })),
        ]);
        if (cancelled) return;
        if (lookup?.match?.to) {
          setRedirecting(true);
          const to = lookup.match.to;
          window.location.replace(to);
        }
      } catch {
        /* best-effort */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);


  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim().toLowerCase();
    if (!q) {
      setFeedback({ type: "error", message: "Please enter a keyword like “SEO”, “pricing”, or “web design”." });
      return;
    }
    if (q.length < 2) {
      setFeedback({ type: "error", message: "Try a longer keyword — at least 2 characters." });
      return;
    }

    // Lightweight keyword routing to the most relevant page.
    const routes: Array<{ keys: string[]; to: string }> = [
      { keys: ["web", "design", "site", "website"], to: "/colorado-springs-web-design" },
      { keys: ["seo", "rank", "ranking", "search"], to: "/colorado-springs-seo" },
      { keys: ["map", "gbp", "google business", "3-pack", "3 pack"], to: "/colorado-springs-google-maps" },
      { keys: ["ppc", "ads", "google ads", "adwords"], to: "/colorado-springs-ppc" },
      { keys: ["social", "facebook", "instagram", "digital marketing"], to: "/digital-marketing" },
      { keys: ["ai", "geo", "chatgpt", "perplexity"], to: "/geo-ai" },
      { keys: ["price", "pricing", "cost", "budget"], to: "/pricing" },
      { keys: ["review", "reputation"], to: "/reviews" },
      { keys: ["case", "results", "portfolio"], to: "/case-studies" },
      { keys: ["chris", "about", "team", "owner"], to: "/about" },
      { keys: ["contact", "call", "quote", "email"], to: "/contact" },
      { keys: ["learn", "guide", "blog", "article"], to: "/learn" },
      { keys: ["industry", "industries"], to: "/industries" },
      { keys: ["location", "city", "area", "service area"], to: "/service-areas" },
    ];

    const match = routes.find((r) => r.keys.some((k) => q.includes(k)));
    if (!match) {
      setFeedback({
        type: "info",
        message: "No exact match — taking you to our Learning Hub where you can browse everything.",
      });
    } else {
      setFeedback(null);
    }
    navigate({ to: match ? match.to : "/learn" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-brand-black to-brand-black-deep py-16 sm:py-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_color-mix(in_oklab,var(--brand-gold)_15%,transparent),_transparent_60%)]" />
          <div className="relative mx-auto max-w-4xl px-4 text-center">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand-gold">
              404 — Page Not Found
            </p>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
              Looks like this page took a hike.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-white/90 sm:text-lg">
              The page you're looking for moved, was renamed, or never existed. Let's get you back
              on track — search below, jump to a popular page, or talk to a real Colorado strategist.
            </p>

            {/* Search */}
            <form
              onSubmit={handleSearch}
              className="mx-auto mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
              role="search"
              aria-label="Site search"
            >
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand-black/50" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (feedback) setFeedback(null);
                  }}
                  placeholder="Search for a service, page, or topic (e.g. “SEO pricing”)"
                  aria-label="Search the site"
                  aria-invalid={feedback?.type === "error" ? true : undefined}
                  aria-describedby={feedback ? "search-feedback" : undefined}
                  maxLength={120}
                  className={`h-14 w-full rounded-md border-2 bg-white pl-12 pr-4 text-base text-foreground placeholder:text-brand-black/50 shadow-elegant transition-colors focus:outline-none focus:ring-4 ${
                    feedback?.type === "error"
                      ? "border-destructive focus:border-destructive focus:ring-destructive/30"
                      : "border-white/20 focus:border-brand-gold focus:ring-brand-gold/40"
                  }`}
                />
              </div>
              <button
                type="submit"
                className="group inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-brand-gold/50"
              >
                Search
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
            {feedback && (
              <p
                id="search-feedback"
                role={feedback.type === "error" ? "alert" : "status"}
                className={`mx-auto mt-3 max-w-xl text-sm font-medium ${
                  feedback.type === "error" ? "text-destructive-foreground bg-destructive/90 rounded-md px-3 py-2" : "text-white/80"
                }`}
              >
                {feedback.message}
              </p>
            )}
          </div>
        </section>

        {/* Popular Colorado pages */}
        <section className="border-b border-border bg-brand-cream/40 py-14">
          <div className="mx-auto max-w-6xl px-4">
            <p className="mb-3 text-center text-xs font-bold uppercase tracking-widest text-brand-gold-deep">
              Popular Colorado pages
            </p>
            <h2 className="mb-2 text-center text-2xl font-bold text-foreground sm:text-3xl">
              Find your city &amp; service
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-center text-sm text-muted-foreground">
              Jump straight to the page locals visit most — Web Design, SEO, and Google Maps for the
              biggest Front Range markets.
            </p>

            <div className="grid gap-6 lg:grid-cols-2">
              {POPULAR_CITIES.map((city) => (
                <div
                  key={city.slug}
                  className="rounded-xl border border-border bg-card p-6 shadow-card transition-shadow hover:shadow-card-hover"
                >
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground">
                    <MapPin className="h-5 w-5 text-brand-gold" />
                    {city.name}
                  </h3>
                  <ul className="grid gap-2 sm:grid-cols-3">
                    {CITY_SERVICES.map((svc) => {
                      const Icon = svc.icon;
                      const href = `/${city.slug}-${svc.suffix}`;
                      return (
                        <li key={svc.suffix}>
                          <a
                            href={href}
                            className="group flex h-full flex-col items-start gap-1 rounded-md border border-border bg-background px-3 py-2.5 transition-colors hover:border-brand-gold hover:bg-brand-gold/5"
                          >
                            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-gold-deep">
                              <Icon className="h-3.5 w-3.5" />
                              {svc.label}
                            </span>
                            <span className="flex items-center gap-1 text-sm font-semibold text-foreground">
                              {city.name}
                              <ArrowRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                            </span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/service-areas"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-gold-deep hover:text-brand-gold"
              >
                See all Colorado service areas
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Top destinations */}
        <section className="border-b border-border py-14">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="mb-2 text-center text-2xl font-bold text-foreground sm:text-3xl">
              Popular destinations
            </h2>
            <p className="mb-8 text-center text-sm text-muted-foreground">
              Jump straight to what most visitors are looking for.
            </p>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {TOP_DESTINATIONS.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="group flex h-full items-start gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-brand-gold/60 hover:bg-card/80"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-brand-gold/15 text-brand-gold">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center gap-2 text-base font-semibold text-foreground">
                          {item.label}
                          <ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                        </span>
                        <span className="mt-1 block text-sm text-muted-foreground">
                          {item.desc}
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-brand-black py-16">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Still can't find what you need?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-white/90">
              Book a free 30-minute strategy call. We'll review your website, your local rankings,
              and tell you exactly what's working — and what's not. No pitch, no pressure.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contact"
                className="group inline-flex h-14 items-center justify-center gap-2 rounded-md bg-gradient-gold px-8 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                Request My Free Strategy Call
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+17194193935"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-md border border-brand-gold/50 bg-transparent px-7 text-sm font-bold uppercase tracking-wider text-brand-gold transition-colors hover:bg-brand-gold/10"
               aria-label="Call (719) 419-3935">
                <Phone className="h-4 w-4" />
                (719) 419-3935
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-widest text-white/85">
              Trusted by Colorado businesses since 2006
            </p>
            <div className="mt-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-white/90 transition-colors hover:text-brand-gold"
              >
                <Home className="h-4 w-4" />
                Or head back to the homepage
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
